import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { isSafeImageUri } from '../utils';

interface ItemDefinitionFormProps {
  initialValues?: {
    name?: string;
    description?: string;
    fungibility?: string;
    thumbnailUri?: string;
    imageUri?: string;
    maxSupply?: number | null;
    metadata?: string;
  };
  onSubmit: (values: Record<string, unknown>) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
}

export function ItemDefinitionForm({
  initialValues,
  onSubmit,
  onCancel,
  isEdit = false,
}: ItemDefinitionFormProps) {
  const [name, setName] = useState(initialValues?.name || '');
  const [description, setDescription] = useState(initialValues?.description || '');
  const [fungibility, setFungibility] = useState(initialValues?.fungibility || 'fungible');
  const [thumbnailUri, setThumbnailUri] = useState(initialValues?.thumbnailUri || '');
  const [imageUri, setImageUri] = useState(initialValues?.imageUri || '');
  const [maxSupply, setMaxSupply] = useState(
    initialValues?.maxSupply != null ? String(initialValues.maxSupply) : '',
  );
  const [metadataJson, setMetadataJson] = useState(initialValues?.metadata || '{}');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    let metadata: Record<string, unknown> = {};
    try {
      metadata = JSON.parse(metadataJson);
    } catch {
      setError('Invalid metadata JSON');
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({
        name: name.trim(),
        description: description.trim() || undefined,
        ...(isEdit ? {} : { fungibility }),
        thumbnailUri: thumbnailUri.trim() || undefined,
        imageUri: imageUri.trim() || undefined,
        maxSupply: maxSupply ? parseInt(maxSupply, 10) : null,
        metadata,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="defName">Name</Label>
        <Input id="defName" value={name} onChange={(e) => setName(e.target.value)} required />
      </div>
      <div className="space-y-2">
        <Label htmlFor="defDescription">Description</Label>
        <Textarea
          id="defDescription"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
        />
      </div>
      {!isEdit && (
        <div className="space-y-2">
          <Label>Fungibility</Label>
          <Select value={fungibility} onValueChange={setFungibility}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fungible">Fungible (quantity-tracked)</SelectItem>
              <SelectItem value="non_fungible">Non-Fungible (unique instances)</SelectItem>
            </SelectContent>
          </Select>
        </div>
      )}
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="thumbUri">Thumbnail URI</Label>
          <Input
            id="thumbUri"
            value={thumbnailUri}
            onChange={(e) => setThumbnailUri(e.target.value)}
            placeholder="https://..."
          />
          {isSafeImageUri(thumbnailUri) && (
            <img
              src={thumbnailUri.trim()}
              alt="Thumbnail preview"
              className="h-16 w-16 rounded object-cover border"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              onLoad={(e) => {
                (e.target as HTMLImageElement).style.display = '';
              }}
            />
          )}
        </div>
        <div className="space-y-2">
          <Label htmlFor="imgUri">Image URI</Label>
          <Input
            id="imgUri"
            value={imageUri}
            onChange={(e) => setImageUri(e.target.value)}
            placeholder="https://..."
          />
          {isSafeImageUri(imageUri) && (
            <img
              src={imageUri.trim()}
              alt="Image preview"
              className="max-h-32 rounded object-contain border"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
              onLoad={(e) => {
                (e.target as HTMLImageElement).style.display = '';
              }}
            />
          )}
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="maxSupply">Max Supply (blank = unlimited)</Label>
        <Input
          id="maxSupply"
          type="number"
          min="1"
          value={maxSupply}
          onChange={(e) => setMaxSupply(e.target.value)}
        />
      </div>
      <div className="space-y-2">
        <Label htmlFor="metadata">Metadata (JSON)</Label>
        <Textarea
          id="metadata"
          value={metadataJson}
          onChange={(e) => setMetadataJson(e.target.value)}
          rows={3}
          className="font-mono text-xs"
        />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex gap-2 justify-end">
        <Button type="button" variant="outline" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={submitting}>
          {submitting ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </Button>
      </div>
    </form>
  );
}
